import { Box, Flex, useToast } from '@lego/klik-ui';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from '@/components/common';
import { BreadCrumb } from '@/components/common/BreadCrumb';
import { ServiceTagsTable } from '@/components/common/table/ServiceTagsTable';
import { RepositoryDetails } from '@/components/repository';
import { useDocumentTitle } from '@/hooks/common';
import { useRepository } from '@/hooks/repository';
import { useRegisterPageContext } from '@/providers/PageContextProvider';
import { paths } from '@/utility/global_paths';
import { ToastOptions } from '@/utility/ToastOptions';

const RepositoryPageContent = ({
  workspaceId,
  repositoryId,
}: {
  workspaceId: number;
  repositoryId: number;
}) => {
  const { data, error, isLoading } = useRepository(workspaceId, repositoryId);
  const dataReady = !isLoading && !error && data;
  const repository = data;

  useRegisterPageContext(repository);
  useDocumentTitle(repository?.name ?? 'Repository');

  return (
    <Box>
      {!dataReady ? (
        <Loader />
      ) : (
        <Box>
          <Box>
            <BreadCrumb
              crumbs={[
                { label: 'Workspaces', path: paths.workspaces },
                {
                  label: `Workspace #${workspaceId}`,
                  path: paths.workspaceById(workspaceId),
                },
                {
                  label: `Repository #${repository!.id}`,
                  path: paths.repositoryById(workspaceId, repository!.id),
                },
              ]}
            />
          </Box>
          <Flex direction="column" gap={8} marginY={8}>
            <RepositoryDetails repository={repository!} />
            <ServiceTagsTable imageName={repository!.imageName} tags={repository!.tags} />
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export const RepositoryPage = () => {
  const { workspaceId, repositoryId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const parsedWorkspaceId = workspaceId ? Number.parseInt(workspaceId, 10) : null;
  const parsedRepositoryId = repositoryId ? Number.parseInt(repositoryId, 10) : null;

  const invalidWorkspace = !workspaceId || !parsedWorkspaceId;
  const invalidRepository = !repositoryId || !parsedRepositoryId;

  useEffect(() => {
    if (invalidWorkspace) {
      toast(ToastOptions.error('Invalid WorkspaceId', 'An invalid ID was specified'));
      navigate(paths.workspaces);
    } else if (invalidRepository) {
      toast(ToastOptions.error('Invalid RepositoryId', 'An invalid ID was specified'));
      navigate(paths.workspaceById(parsedWorkspaceId!));
    }
  }, [invalidWorkspace, invalidRepository, parsedWorkspaceId, navigate, toast]);

  if (invalidWorkspace || invalidRepository) {
    return null;
  }

  return (
    <RepositoryPageContent repositoryId={parsedRepositoryId} workspaceId={parsedWorkspaceId} />
  );
};
